/**
 * People's words (slice 6a §6, artboards 70–75), apart from the components
 * so they're tested alone: when someone was last here, how an invite
 * stands, the count line, and the join page's line of names.
 */
import type { Invite, Person, Sent } from "./api";
import type { ToastMessage } from "./toast";

const DAY = 24 * 60 * 60 * 1000;

const monthDay = (at: Date, timezone: string) =>
  new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: timezone }).format(
    at,
  );

/** The calendar day of `at` where the workspace is, as a sortable number. */
function dayOf(at: Date, timezone: string): number {
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: timezone,
  }).format(at);
  return Date.parse(`${parts}T00:00:00Z`) / DAY;
}

/** "Here now", "12 minutes ago", "2 hours ago", "Yesterday", "Oct 3"; "Not yet" before they've signed in. */
export function lastSeen(at: string | null, now: Date, timezone: string): string {
  if (!at) return "Not yet";
  const seen = new Date(at);
  const ago = now.getTime() - seen.getTime();
  if (ago < 5 * 60 * 1000) return "Here now";
  if (ago < 60 * 60 * 1000) return `${Math.floor(ago / 60_000)} minutes ago`;
  const days = dayOf(now, timezone) - dayOf(seen, timezone);
  if (days === 0) {
    const hours = Math.floor(ago / 3_600_000);
    return hours === 1 ? "An hour ago" : `${hours} hours ago`;
  }
  if (days === 1) return "Yesterday";
  return monthDay(seen, timezone);
}

/** A person's apps (§6.1): their names, or "Shared apps only". */
export const appsLine = (p: Pick<Person, "apps">) =>
  p.apps.length > 0 ? p.apps.join(", ") : "Shared apps only";

export type InviteState = {
  /** The status word: copper "Invited", or "Expired". */
  label: "Invited" | "Expired";
  /** "Sent Oct 7 · 6 days left", "Couldn't send", "Sent Oct 1 · expired". */
  detail: string;
  /** The phone's shorter line after the label: "6 days left". */
  short: string;
  failed: boolean;
};

/** How an invite stands (§6.1, §7): sent and how long it has, unsent, or expired. */
export function inviteState(i: Invite, now: Date, timezone: string): InviteState {
  if (!i.sent_at) {
    return { label: "Invited", detail: "Couldn't send", short: "Couldn't send", failed: true };
  }
  const sent = `Sent ${monthDay(new Date(i.sent_at), timezone)}`;
  const left = new Date(i.expires_at).getTime() - now.getTime();
  if (i.expired || left <= 0) {
    return { label: "Expired", detail: sent, short: "Resend for a new link", failed: false };
  }
  const days = Math.ceil(left / DAY);
  const remaining = days === 1 ? "1 day left" : `${days} days left`;
  return { label: "Invited", detail: `${sent} · ${remaining}`, short: remaining, failed: false };
}

/** "Invited by you" or "Invited by Maya Hollis". */
export function invitedBy(i: Invite, viewerId: string): string {
  if (!i.invited_by) return "Invited";
  return i.invited_by.id === viewerId ? "Invited by you" : `Invited by ${i.invited_by.name}`;
}

/** "3 people · 1 invited"; "1 person". */
export function crewCount(people: number, invites: number): string {
  const crew = `${people} ${people === 1 ? "person" : "people"}`;
  return invites > 0 ? `${crew} · ${invites} invited` : crew;
}

const firstName = (name: string) => name.trim().split(/\s+/)[0] || name;

/** "Maya, Dev and Ana" (§6.3): the crew's first names. */
function firstNames(people: Array<{ name: string }>): string {
  const names = people.map((p) => firstName(p.name));
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
}

/** "Maya, Dev, Ana and their Chief of Staff" (73), "Maya, Dev and Ana are here" (73b). */
export function crewLine(people: Array<{ name: string }>, phone: boolean): string {
  const names = people.map((p) => firstName(p.name));
  if (phone) {
    if (names.length === 1) return `${names[0]} is here`;
    return `${firstNames(people)} are here`;
  }
  return `${names.join(", ")} and their Chief of Staff`;
}

/** The toast after a send (§6.2): "Invite sent to <email>.", with Copy link in mock mode. */
export function sentToast(out: Sent, again = false): ToastMessage {
  const email = out.invite.email;
  if (!out.invite.sent_at) {
    return { id: Date.now(), text: `Couldn't send the invite to ${email}. Try Resend.` };
  }
  return {
    id: Date.now(),
    text: `Invite sent${again ? " again" : ""} to ${email}.`,
    link: out.link,
  };
}
